<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useSearch } from '@ilteducation/content-search';
import type { VideoV1 } from '@ilteducation/content-registry-types';
import { useVideos } from '@ilteducation/videos';
import type { Video } from '@ilteducation/videos';
import { DialogCloseButton, DialogDrawer } from '@ilteducation/components';
import AddVideoDialogContent from './AddVideoDialogContent.vue';
import { storeToRefs } from 'pinia';
import debounce from 'lodash/debounce';
import axios from 'axios';
import { configHelper } from '@ilteducation/config';
import type { Exercise } from '@ilteducation/exercise-service-types';
import useLocale from '../../../composables/use-locale';

const exerciseApi = configHelper.getApiUrl('exercises');

const props = defineProps<{
  modelValue: boolean;
  excludeResourceIds?: string[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'select', video: { resourceId: string; title: string; thumbnail?: string }): void;
}>();

const { t } = useLocale();

const searchInput = ref('');
const videoSearchStore = useSearch<VideoV1>('Video');
const { result: searchResults, isLoading } = storeToRefs(videoSearchStore);

const { addVideos } = useVideos();
const { next } = videoSearchStore;

const locallyAddedVideos = ref<Set<string>>(new Set());
const checkingVideoId = ref<string | null>(null);
const noQuizWarning = ref<string | null>(null);

// Convert VideoV1 results to Video objects with proper string titles and thumbnail URLs
const convertedVideos = ref<Video[]>([]);

watch(
  searchResults,
  (newResults) => {
    if (newResults && newResults.length > 0) {
      convertedVideos.value = addVideos(newResults);
    } else {
      convertedVideos.value = [];
    }
  },
  { immediate: true },
);

// HARDCODED: Categories without quizzes - exclude from Quizter search
const EXCLUDED_CATEGORY_IDS = [
  'yC1YytG74jzJi00Qx4kSk', // Slöjd
  'kkiFvljMc4lgYWpfAxUIr', // Fordon och Transport
];

const filteredResults = computed(() => {
  return convertedVideos.value.filter(
    (video) => !video.categories.some((categoryId) => EXCLUDED_CATEGORY_IDS.includes(categoryId)),
  );
});

const isVideoAdded = (resourceId: string) => {
  return props.excludeResourceIds?.includes(resourceId) || locallyAddedVideos.value.has(resourceId);
};

const onSearch = (input: string) => {
  videoSearchStore.search(input || '', {}, 20, [{ field: 'lastUpdatedAt', order: 'desc' }]);
};

const debouncedSearch = debounce((input: string) => {
  onSearch(input);
}, 300);

const handleAddVideo = async (video: Video) => {
  // Set loading state for this specific video
  checkingVideoId.value = video.resourceId;
  noQuizWarning.value = null;

  try {
    // Check if the video has a quiz via the exercise API
    const response = await axios.get<{ data: Exercise[] }>(`${exerciseApi}/?resourceId=${video.resourceId}`);
    const hasQuiz = response.data.data?.some((e) => e.type === 'quiz');

    if (!hasQuiz) {
      // Show warning - video has no quiz
      noQuizWarning.value = video.title;
      return;
    }

    // Video has a quiz, proceed to add it
    emit('select', {
      resourceId: video.resourceId,
      title: video.title,
      thumbnail: video.thumbnail || video.previewImage,
    });
    locallyAddedVideos.value.add(video.resourceId);
  } catch (error) {
    console.error('Error checking for quiz:', error);
    // Show warning on error as well
    noQuizWarning.value = video.title;
  } finally {
    checkingVideoId.value = null;
  }
};

const handleLoadMore = () => {
  next();
};

const handleClose = () => {
  emit('update:modelValue', false);
  searchInput.value = '';
  locallyAddedVideos.value.clear();
  noQuizWarning.value = null;
};

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      searchInput.value = '';
      onSearch('');
    }
  },
  { immediate: true },
);
</script>

<template>
  <DialogDrawer
    :full-height="true"
    :model-value="modelValue"
    :titleText="t('quizter.add_video_clarification')"
    cardColor="rgb(219, 219, 219)"
    maxWidth="700"
    @close="handleClose"
  >
    <template #dialogTopbar>
      <DialogCloseButton @click="handleClose" icon="fa-whiteboard fa-times" />
      <v-card-title class="pb-6 text-center position-relative">
        <h2 class="chalkduster-font text-black ma-0 text-wrap px-14">{{ t('quizter.add_video_clarification') }}</h2>
      </v-card-title>
    </template>
    <keep-alive>
      <AddVideoDialogContent
        v-model:search-input="searchInput"
        :is-loading="isLoading"
        :no-quiz-warning="noQuizWarning"
        :filtered-results="filteredResults"
        :checking-video-id="checkingVideoId"
        :is-video-added="isVideoAdded"
        @search="debouncedSearch"
        @close-warning="noQuizWarning = null"
        @add-video="handleAddVideo"
        @load-more="handleLoadMore"
      />
    </keep-alive>
  </DialogDrawer>
</template>
